/**
 * FN-FE-02 + FN-FE-15 — Topbar — PROTOTÍPUS SZERINTI navy layout
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureContext } from '../../contexts/FeatureContext';
import { get } from '../../lib/api';
import NotificationPanel from '../NotificationPanel';
import { usePwaInstall } from '../PwaInstallPrompt';

interface TopbarProps { onMenuClick: () => void; onNotificationClick?: () => void }

interface SearchResult {
  type: 'partner' | 'invoice' | 'project' | 'item' | 'document';
  id: string; title: string; subtitle: string; url: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  partner: 'Partnerek', invoice: 'Számlák', project: 'Projektek', item: 'Cikkek', document: 'Dokumentumok',
};

const BASE_MENU = [
  { label: 'Áttekintő', items: [{ label: 'Vezérlőpult', href: '/dashboard' }] },
  { label: 'Partnerek', items: [{ label: 'Partnertörzs', href: '/partnerek' }, { label: 'Modulum Hálózat', href: '/halozat' }] },
  { label: 'Értékesítés', items: [
    { label: 'Cikkek', href: '/cikkek' },
    { label: 'Megrendelések', href: '/megrendelesek' },
    { label: 'Szállítólevelek', href: '/szallitolevelek' },
  ]},
  { label: 'Pénzügy', items: [
    { label: 'Számlák', href: '/szamlak' },
    { label: 'Házipénztár', href: '/hazipenztar' },
    { label: 'Bejövő számlák', href: '/bejovo-szamlak' },
    { label: 'Kintlévőség', href: '/kintlevoseg' },
    { label: 'Költségallokáció', href: '/allokacio' },
  ]},
  { label: 'Projektek', items: [
    { label: 'Összes projekt', href: '/projektek' },
  ]},
  { label: 'Beszerzés', items: [{ label: 'Megrendelések', href: '/beszerzes' }] },
  { label: 'Dokumentumok', items: [{ label: 'Dokumentumtár', href: '/dokumentumok' }] },
  { label: 'Csapat', items: [
    { label: 'Felhasználók', href: '/beallitasok/felhasznalok' },
  ]},
  { label: 'Beállítások', items: [
    { label: 'Általános', href: '/beallitasok' },
    { label: 'Cég-profil', href: '/beallitasok/ceg' },
    { label: 'NAV-kapcsolat', href: '/beallitasok/nav' },
    { label: 'Értesítések', href: '/beallitasok/ertesitesek' },
    { label: 'Előfizetés', href: '/beallitasok/szamlazas' },
    { label: 'Audit-napló', href: '/beallitasok/audit' },
  ]},
];

const IDOPONT_MENU = { label: 'Időpontfoglaló', items: [{ label: '📅 Foglalási profil', href: '/idopontfoglalas' }] };

export default function Topbar({ onMenuClick, onNotificationClick }: TopbarProps) {
  const { user, logout } = useAuth();
  const { activeFeatures } = useFeatureContext();
  const hasIdopont = activeFeatures.includes('idopont_basic') || activeFeatures.includes('idopont_pro');
  const MENU = hasIdopont
    ? [...BASE_MENU.slice(0, 7), IDOPONT_MENU, ...BASE_MENU.slice(7)]
    : BASE_MENU;
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(-1);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [perTenantCounts, setPerTenantCounts] = useState<Record<string, number>>({});
  const { canInstall, promptInstall } = usePwaInstall();
  const menuRef = useRef<HTMLElement>(null);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenu(-1);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Cross-tenant értesítés szám — 15 másodpercenként frissül
  useEffect(() => {
    const fetchCounts = () => {
      get<{ total: number; counts: Record<string, number> }>('/auth/all-notification-counts')
        .then(d => { setUnreadCount(d.total); setPerTenantCounts(d.counts || {}); })
        .catch(() => {
          // Fallback: csak az aktív tenant
          get<{ count: number }>('/notifications/unread-count').then(d => setUnreadCount(d.count)).catch(() => {});
        });
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 15_000);
    return () => clearInterval(t);
  }, []);

  // Cmd+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true); setQuery(''); setResults([]);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Debounced search
  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      const allResults: SearchResult[] = [];
      try {
        const [partners, invoices, projects, items, docs] = await Promise.allSettled([
          get<any>(`/partners?search=${encodeURIComponent(query)}`),
          get<any>(`/invoices?search=${encodeURIComponent(query)}`),
          get<any>(`/projects?search=${encodeURIComponent(query)}`),
          get<any>(`/items?search=${encodeURIComponent(query)}`),
          get<any>(`/documents?search=${encodeURIComponent(query)}`),
        ]);
        const extract = (r: PromiseSettledResult<any>) => r.status === 'fulfilled' ? (Array.isArray(r.value) ? r.value : r.value?.data || []) : [];
        for (const p of extract(partners).slice(0, 5)) allResults.push({ type: 'partner', id: p.id, title: p.name, subtitle: p.tax_number || '', url: '/partnerek' });
        for (const inv of extract(invoices).slice(0, 5)) allResults.push({ type: 'invoice', id: inv.id, title: inv.invoice_number || inv.number, subtitle: `${inv.partner_name || ''}`, url: '/szamlak' });
        for (const proj of extract(projects).slice(0, 5)) allResults.push({ type: 'project', id: proj.id, title: proj.name, subtitle: proj.project_code || '', url: '/projektek' });
        for (const it of extract(items).slice(0, 5)) allResults.push({ type: 'item', id: it.id, title: it.name, subtitle: it.sku || '', url: '/cikkek' });
        for (const doc of extract(docs).slice(0, 5)) allResults.push({ type: 'document', id: doc.id, title: doc.name, subtitle: '', url: '/dokumentumok' });
      } catch { /* silent */ }
      setResults(allResults); setSelectedIdx(0); setSearching(false);
    }, 200);
    return () => clearTimeout(timer);
  }, [query]);

  const navigateToResult = useCallback((result: SearchResult) => {
    setSearchOpen(false); navigate(result.url);
  }, [navigate]);

  const handleSearchKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setSearchOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && results.length > 0) { e.preventDefault(); navigateToResult(results[selectedIdx]); }
  };

  const grouped = results.reduce<Record<string, SearchResult[]>>((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {});

  const initials = user?.name ? user.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : 'U';

  // Elérhetővé tesszük a per-tenant számokat globálisan (TenantSwitcherPage számára)
  useEffect(() => {
    (window as any).__modulumNotifCounts = perTenantCounts;
  }, [perTenantCounts]);

  return (
    <>
      {/* ===== NAVY TOPBAR ===== */}
      <header className="topbar">
        <div className="brand">
          <svg width="30" height="30" viewBox="0 0 100 100">
            <path d="M16 88 L16 40 L50 64" fill="none" stroke="#fff" strokeWidth="17" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M50 64 L84 16 L84 64" fill="none" stroke="#F47B20" strokeWidth="17" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="logo-wm">modulum<span className="tld">.hu</span></span>
        </div>

        {/* Desktop menu */}
        <nav ref={menuRef} id="topMenu" className={`menu${mobileMenuOpen ? ' mobile-open' : ''}`}>
          {MENU.map((m, i) => (
            <div key={i} className={`menu-item${openMenu === i ? ' open' : ''}`}>
              <button className="menu-btn" onClick={() => setOpenMenu(openMenu === i ? -1 : i)}>
                {m.label}<span className="chev" />
              </button>
              <div className="dropdown">
                {m.items.map((item, j) => (
                  <Link
                    key={j}
                    to={item.href}
                    className={`dd-item${location.pathname === item.href ? ' active' : ''}`}
                    onClick={() => { setOpenMenu(-1); setMobileMenuOpen(false); }}
                    style={location.pathname === item.href ? { background: 'var(--mist)' } : undefined}
                  >
                    <span className="dot" style={location.pathname === item.href ? { background: 'var(--orange)' } : undefined} />
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="topbar-right">
          <div className="global-search" onClick={() => { setSearchOpen(true); setQuery(''); setResults([]); }} style={{ cursor: 'pointer' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4">
              <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>
            </svg>
            <input readOnly value="" placeholder="Keresés…" style={{ background: 'none', border: 'none', color: '#fff', fontSize: '13px', width: '100%', fontFamily: "'Hanken Grotesk', sans-serif", cursor: 'pointer', outline: 'none' }} tabIndex={-1} />
            <span className="kb">⌘K</span>
          </div>

          {/* Értesítések gomb */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setNotifOpen(o => !o)}
              title="Értesítések"
              style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '8px', padding: '7px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#fff', fontSize: '15px', position: 'relative' }}
              aria-label="Értesítések"
            >
              🔔
              {unreadCount > 0 && (
                <span style={{ position: 'absolute', top: '3px', right: '3px', background: 'var(--orange)', color: '#fff', borderRadius: '50%', fontSize: '9px', fontWeight: 700, minWidth: '14px', height: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            {notifOpen && (
              <NotificationPanel
                onClose={() => setNotifOpen(false)}
                onCountChange={count => setUnreadCount(count)}
              />
            )}
          </div>

          {/* Vonalkód beolvasó gomb — csak mobilon */}
          <button onClick={() => navigate('/beolvaso')} title="Vonalkód beolvasó"
            className="topbar-cam"
            style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '8px', padding: '7px 10px', cursor: 'pointer', alignItems: 'center', gap: '5px', color: '#fff', fontSize: '14px' }}>
            📷
          </button>

          {/* User avatar + dropdown */}
          <div style={{ position: 'relative' }}>
            <button className="avatar" onClick={() => setUserMenuOpen(!userMenuOpen)}>{initials}</button>
            {userMenuOpen && (
              <div style={{ position: 'absolute', right: 0, top: '42px', width: '220px', background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 'var(--r)', boxShadow: '0 18px 40px -14px rgba(20,35,63,.35)', padding: '7px', zIndex: 100 }}>
                <div style={{ padding: '9px 11px', fontSize: '12px', color: 'var(--slate)', borderBottom: '1px solid var(--line-2)', marginBottom: '4px' }}>
                  {user?.name || 'Felhasználó'}
                </div>
                <button className="dd-item" onClick={() => { setUserMenuOpen(false); navigate('/beallitasok'); }}>
                  <span className="dot" /> Beállítások
                </button>
                <button className="dd-item" onClick={() => { setUserMenuOpen(false); navigate('/beallitasok/szamlazas'); }}>
                  <span className="dot" /> Előfizetés
                </button>
                <button className="dd-item" onClick={() => { setUserMenuOpen(false); navigate('/beallitasok/felhasznalok'); }}>
                  <span className="dot" /> Felhasználók
                </button>
                <div style={{ borderTop: '1px solid var(--line-2)', marginTop: '4px', paddingTop: '4px' }}>
                  <button className="dd-item" onClick={() => { setUserMenuOpen(false); navigate('/cegvaltas'); }}>
                    <span className="dot" /> Cég váltás
                  </button>
                </div>
                {canInstall && (
                  <div style={{ borderTop: '1px solid var(--line-2)', marginTop: '4px', paddingTop: '4px' }}>
                    <button className="dd-item" onClick={() => { setUserMenuOpen(false); promptInstall(); }}>
                      <span className="dot" /> App telepítése
                    </button>
                  </div>
                )}
                <div style={{ borderTop: '1px solid var(--line-2)', marginTop: '4px', paddingTop: '4px' }}>
                  <button className="dd-item" onClick={() => { setUserMenuOpen(false); logout(); }} style={{ color: 'var(--red)' }}>
                    <span className="dot" style={{ background: 'var(--red)' }} /> Kijelentkezés
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button className="hamb" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menü">
            <span />
          </button>
        </div>
      </header>

      {/* ===== SEARCH MODAL (Cmd+K) ===== */}
      {searchOpen && (
        <div className="overlay show" onClick={() => setSearchOpen(false)} style={{ display: 'flex' }}>
          <div className="modal" style={{ maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', padding: '0 16px', borderBottom: '1px solid var(--line)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--slate)" strokeWidth="2.4" style={{ flexShrink: 0, marginRight: '10px' }}>
                <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>
              </svg>
              <input
                autoFocus value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleSearchKey}
                placeholder="Keress számlát, partnert, projektet..."
                style={{ flex: 1, padding: '14px 0', fontSize: '14px', border: 'none', outline: 'none', fontFamily: "'Hanken Grotesk', sans-serif", color: 'var(--navy)' }}
              />
              <span className="kb" style={{ fontSize: '10px', color: 'var(--slate-2)', border: '1px solid var(--line)', padding: '2px 6px', borderRadius: '4px' }}>Esc</span>
            </div>
            <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
              {query.length < 2 ? (
                <div style={{ padding: '24px', textAlign: 'center', fontSize: '13px', color: 'var(--slate)' }}>Kezdj gépelni a kereséshez...</div>
              ) : searching ? (
                <div style={{ padding: '24px', textAlign: 'center', fontSize: '13px', color: 'var(--slate)' }}>Keresés...</div>
              ) : results.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', fontSize: '13px', color: 'var(--slate)' }}>Nincs találat</div>
              ) : (
                <div style={{ padding: '6px 0' }}>
                  {Object.entries(grouped).map(([type, items]) => (
                    <div key={type}>
                      <div style={{ padding: '6px 16px', fontSize: '10px', fontWeight: 700, color: 'var(--slate-2)', letterSpacing: '.1em', textTransform: 'uppercase' }}>
                        {CATEGORY_LABELS[type] || type} ({items.length})
                      </div>
                      {items.map((item) => {
                        const globalIdx = results.indexOf(item);
                        return (
                          <button key={item.id} className="dd-item" onClick={() => navigateToResult(item)}
                            style={{ width: '100%', ...(globalIdx === selectedIdx ? { background: 'var(--mist)' } : {}) }}>
                            <span className="dot" style={globalIdx === selectedIdx ? { background: 'var(--orange)' } : undefined} />
                            <span style={{ flex: 1 }}>
                              <div style={{ fontWeight: 700, fontSize: '13px' }}>{item.title}</div>
                              {item.subtitle && <div style={{ fontSize: '11px', color: 'var(--slate)' }}>{item.subtitle}</div>}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
